/**
 * Louise API Client
 * Handles authenticated API requests to https://dashboard.api.dev.louise.life
 */

import { useStytch, useStytchUser } from "./useStytch";
import type { paths as ApiPaths } from "../openapi/schema";
import createClient from "openapi-fetch";
import { useMemo } from "react";
import { usePostHog } from "posthog-react-native";
import { fetchApiResponse } from "./api-error";
import { normalizeApiPath } from "../analytics/apiPath";

export { retryLouiseQuery } from "./retry-policy";

const API_BASE_URL = process.env.EXPO_PUBLIC_API_BASE_URL!;

const ollyUserIdClaim = "patient_id_in_dashboard_api";
const ollyTenantIdClaim = "tenant_slug";
/** Default tenant for legacy /{tenantId}/... paths. Remove when fully migrated to new paths. */
const DEFAULT_TENANT_ID = "olly";

let sessionRefreshPromise: Promise<void> | null = null;

function refreshSession(stytch: ReturnType<typeof useStytch>): Promise<void> {
  if (!sessionRefreshPromise) {
    sessionRefreshPromise = stytch.session
      .authenticate({ session_duration_minutes: 60 * 24 * 30 })
      .then(() => undefined)
      .finally(() => {
        sessionRefreshPromise = null;
      });
  }
  return sessionRefreshPromise;
}
export function getIdsFromMetadata(metadata: Record<string, unknown>) {
  const { [ollyTenantIdClaim]: tenantId, [ollyUserIdClaim]: patientId } =
    metadata;
  return {
    tenantId: (tenantId as string | undefined) ?? DEFAULT_TENANT_ID,
    patientId: patientId as string | undefined,
    /** @deprecated Use patientId instead */
    userId: patientId as string | undefined,
  };
}

// TODO: refactor with context so we have a single client instance everywehere

/**
 * Hook that returns an authenticated API client with tenant and user context
 *
 * Returns:
 * - client: openapi-fetch client with authentication headers
 * - userId: patient ID from user metadata
 * - tenantId: tenant slug from user metadata
 * - patientId: alias for userId (for backwards compatibility)
 */
export function useApiClient() {
  const stytch = useStytch();
  // Typed non-null but undefined outside a PostHogProvider (unit tests) —
  // every use below is optional-chained so the client works without it.
  const posthog = usePostHog();
  // Reactive user (re-renders when the Stytch user hydrates/changes), so ids
  // derived from it update and queries gated on them enable themselves after
  // a cold page load — getSync() is a one-shot snapshot and never re-renders.
  const { user } = useStytchUser();

  const { tenantId, patientId } = getIdsFromMetadata(
    user?.trusted_metadata ?? {},
  );

  const clients = useMemo(() => {
    const authedFetch = async (input: Request) => {
      const token = stytch.session.getTokens()?.session_jwt;
      // Constructing the authorized Request below disturbs input's body, so
      // keep an untouched clone to rebuild the request from on a 401 retry.
      // Without this, retrying a request that has a body (POST/PUT/PATCH)
      // throws "body already used".
      const retrySource = input.clone();
      const headers = new Headers(input.headers);
      if (token) {
        headers.set("Authorization", `Bearer ${token}`);
      }
      const response = await fetchApiResponse(
        new Request(input, { headers }),
        undefined,
        "Unable to reach Louise",
        "louiseApi.fetch",
      );

      // A restored Stytch session can briefly expose an old JWT while the
      // backend has already rejected it. Refresh once, shared across all
      // queries, so the first page does not fan out into identical 401s.
      if (response.status !== 401 || !token) return response;

      try {
        await refreshSession(stytch);
      } catch {
        return response;
      }

      const refreshedToken = stytch.session.getTokens()?.session_jwt;
      if (!refreshedToken || refreshedToken === token) return response;

      const retryHeaders = new Headers(retrySource.headers);
      retryHeaders.set("Authorization", `Bearer ${refreshedToken}`);
      return fetchApiResponse(
        new Request(retrySource, { headers: retryHeaders }),
        undefined,
        "Unable to reach Louise after refreshing the session",
        "louiseApi.fetchAfterSessionRefresh",
      );
    };

    const options = {
      baseUrl: API_BASE_URL,
      // Instrumented wrapper: every API write emits submitted/succeeded/failed
      // events and every failed read emits api_request_failed, so user actions
      // are observable in PostHog without per-screen wiring. Events carry the
      // full url plus an id-normalized path for grouping — never bodies.
      fetch: async (input: Request) => {
        const method = input.method.toUpperCase();
        const isAction = method !== "GET" && method !== "HEAD";
        const url = input.url;
        const path = normalizeApiPath(new URL(url).pathname);
        const startedAt = Date.now();
        if (isAction) {
          posthog?.capture("action_submitted", { method, path, url });
        }
        try {
          const response = await authedFetch(input);
          const outcome = {
            method,
            path,
            url,
            status: response.status,
            duration_ms: Date.now() - startedAt,
          };
          if (isAction) {
            posthog?.capture(
              response.ok ? "action_succeeded" : "action_failed",
              outcome,
            );
          } else if (!response.ok) {
            posthog?.capture("api_request_failed", outcome);
          }
          return response;
        } catch (error) {
          posthog?.capture(isAction ? "action_failed" : "api_request_failed", {
            method,
            path,
            url,
            duration_ms: Date.now() - startedAt,
            error_message: error instanceof Error ? error.message : "unknown",
          });
          throw error;
        }
      },
    };

    return {
      client: createClient<ApiPaths>(options),
    };
  }, [stytch, posthog]);

  // Stable identity across unrelated re-renders (the reactive user now re-renders
  // this hook more often); ids are primitives and client is already memoized, so
  // consumers can safely use the returned object in dep arrays.
  return useMemo(
    () => ({
      /** @deprecated Use patientId instead */
      userId: patientId as string,
      ...clients,
      tenantId,
      patientId: patientId as string,
    }),
    [clients, tenantId, patientId],
  );
}

export type ApiClient = ReturnType<typeof useApiClient>["client"];
